import { useEffect, useRef } from "react"
import type { IParcel } from "@/types/explore"

interface Google3DMapProps {
  longitude: number
  latitude: number
  googleMapsKey: string
  selectedParcels?: IParcel[]
}

export const Google3DMap = ({ longitude, latitude, googleMapsKey, selectedParcels }: Google3DMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const initMap = () => {
      if (!containerRef.current) return
      containerRef.current.innerHTML = ""

      const gmpMap = document.createElement("gmp-map-3d")
      gmpMap.setAttribute("center", `${latitude}, ${longitude}, 100`)
      gmpMap.setAttribute("tilt", "45")
      gmpMap.setAttribute("mode", "hybrid")
      gmpMap.setAttribute("range", "200")

      if (selectedParcels?.length) {
        for (const parcel of selectedParcels) {
          if (!parcel.geometry) continue
          const coords =
            parcel.geometry.type === "MultiPolygon" ? parcel.geometry.coordinates[0][0] : parcel.geometry.coordinates[0]
          if (!coords?.length) continue

          const polygon = document.createElement("gmp-polygon-3d")
          polygon.setAttribute("stroke-color", "#2563eb")
          polygon.setAttribute("stroke-width", "4")
          polygon.setAttribute("fill-color", "rgba(37, 99, 235, 0.4)")
          polygon.setAttribute("altitude-mode", "clamp-to-ground")
          polygon.setAttribute("path", (coords as number[][]).map((c) => `${c[1]},${c[0]}`).join(" "))
          gmpMap.appendChild(polygon)
        }
      }

      const marker = document.createElement("gmp-marker-3d")
      marker.setAttribute("position", `${latitude}, ${longitude}`)
      marker.setAttribute("altitude-mode", "clamp-to-ground")
      gmpMap.appendChild(marker)

      containerRef.current.appendChild(gmpMap)
    }

    // If the API is already loaded, just init the map
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      initMap()
      return () => {
        if (containerRef.current) containerRef.current.innerHTML = ""
      }
    }

    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsKey}&v=alpha&libraries=maps3d&loading=async`
    script.async = true
    script.onload = initMap
    document.body.appendChild(script)

    return () => {
      if (containerRef.current) containerRef.current.innerHTML = ""
    }
  }, [latitude, longitude, googleMapsKey, selectedParcels])

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
}
